import { accountingApi } from '../../../../config/apiClient';
import { ACCOUNTING_API } from '../../../../config/api';

// Kategori tipi
export interface InventoryCategoryDto {
  id: string;
  companyId: string;
  name: string;
  parentId: string | null;
  parentName?: string;
  level: number;
  isEcommerceEnabled: boolean;
  imageUrl?: string;
  headerImageUrl?: string;
  orderIndex: number;
  description?: string;
  isActive: boolean;
  createdAt: string;
  children?: InventoryCategoryDto[];
  itemCount: number;
}

/**
 * Kategori silme engeli. `code` makine tarafında ayırt etmek için sabittir:
 * subCategories | products | salesScreens | ecommerce
 */
export interface InventoryCategoryDeleteBlocker {
  code: string;
  label: string;
  count: number;
  examples: string[];
}

export interface InventoryCategoryDeleteCheck {
  categoryId: string;
  categoryName: string;
  canDelete: boolean;
  blockers: InventoryCategoryDeleteBlocker[];
}

/**
 * Sunucu silmeyi engellediğinde atılır. Kontrol çağrısı ile silme arasında ürün
 * eklenmesi gibi durumlarda da kullanıcıya gerekçe gösterebilmek için engelleri taşır.
 */
export class CategoryDeleteBlockedError extends Error {
  blockers: InventoryCategoryDeleteBlocker[];

  constructor(message: string, blockers: InventoryCategoryDeleteBlocker[]) {
    super(message);
    this.name = 'CategoryDeleteBlockedError';
    this.blockers = blockers;
  }
}

export interface CreateInventoryCategoryDto {
  companyId: string;
  name: string;
  parentId?: string | null;
  isEcommerceEnabled?: boolean;
  imageUrl?: string;
  headerImageUrl?: string;
  orderIndex?: number;
  description?: string;
  isActive?: boolean;
}

export interface UpdateInventoryCategoryDto {
  companyId: string; // ✅ MULTI-TENANT: Şirket ID'si
  id: string;
  name: string;
  parentId?: string | null;
  isEcommerceEnabled: boolean;
  imageUrl?: string;
  headerImageUrl?: string;
  orderIndex: number;
  description?: string;
  isActive: boolean;
}

// Kategori servis fonksiyonları
export class CategoryService {
  /**
   * Şirkete ait tüm kategorileri getir
   */
  static async getAllCategories(companyId: string): Promise<InventoryCategoryDto[]> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategoriler yükleniyor:', companyId);
      const response = await accountingApi.get<InventoryCategoryDto[]>(
        ACCOUNTING_API.INVENTORY_CATEGORIES
      );
      console.log('✅ [CATEGORY SERVICE] Kategoriler başarıyla yüklendi:', response.data.length);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori yükleme hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategoriler yüklenirken hata oluştu');
    }
  }

  /**
   * Yeni kategori oluştur
   */
  static async createCategory(category: CreateInventoryCategoryDto): Promise<InventoryCategoryDto> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Yeni kategori oluşturuluyor:', category);
      const response = await accountingApi.post<InventoryCategoryDto>(
        ACCOUNTING_API.INVENTORY_CATEGORY_CREATE,
        category
      );
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla oluşturuldu:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori oluşturma hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategori oluşturulurken hata oluştu');
    }
  }

  /**
   * Kategoriyi güncelle - POST kullanıyoruz (sunucu kısıtlaması)
   */
  static async updateCategory(category: UpdateInventoryCategoryDto): Promise<InventoryCategoryDto> {
    try {
      const url = ACCOUNTING_API.INVENTORY_CATEGORY_UPDATE(category.id);
      console.log('🔍 [CATEGORY SERVICE] Kategori güncelleniyor:', category);
      console.log('🔍 [CATEGORY SERVICE] API URL:', url);
      
      const response = await accountingApi.post<InventoryCategoryDto>(url, category);
      
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla güncellendi:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori güncelleme hatası:', error);
      console.error('❌ [CATEGORY SERVICE] Error response:', error.response?.data);
      console.error('❌ [CATEGORY SERVICE] Error status:', error.response?.status);
      throw new Error(error.response?.data?.message || error.response?.data?.error || 'Kategori güncellenirken hata oluştu');
    }
  }

  /**
   * Kategorinin silinebilir olup olmadığını sorar. Engel varsa gerekçeleriyle döner
   * (alt kategori, bağlı ürün, satış ekranı kullanımı, e-ticaret).
   *
   * Bu yalnızca kullanıcıyı önceden bilgilendirmek içindir; asıl engelleme sunucudaki
   * silme ucunda yapılır, dolayısıyla bu çağrı atlanırsa silme yine reddedilir.
   */
  static async checkCategoryDeletable(categoryId: string): Promise<InventoryCategoryDeleteCheck> {
    try {
      const response = await accountingApi.get<{ success: boolean; data: InventoryCategoryDeleteCheck }>(
        ACCOUNTING_API.INVENTORY_CATEGORY_DELETE_CHECK(categoryId)
      );
      return response.data.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori silme kontrolü hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategori silme kontrolü yapılamadı');
    }
  }

  /**
   * Kategoriyi sil - POST kullanıyoruz (sunucu kısıtlaması)
   */
  static async deleteCategory(companyId: string, categoryId: string): Promise<void> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategori siliniyor:', categoryId);
      console.log('🔍 [CATEGORY SERVICE] API URL:', ACCOUNTING_API.INVENTORY_CATEGORY_DELETE(categoryId));
      
      const response = await accountingApi.post(
        ACCOUNTING_API.INVENTORY_CATEGORY_DELETE(categoryId)
      );
      
      console.log('✅ [CATEGORY SERVICE] API yanıtı:', response);
      console.log('✅ [CATEGORY SERVICE] Status:', response.status);
      console.log('✅ [CATEGORY SERVICE] Data:', response.data);
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla silindi');
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori silme hatası:', error);
      console.error('❌ [CATEGORY SERVICE] Error response:', error.response);
      console.error('❌ [CATEGORY SERVICE] Error message:', error.message);

      const blockers = error.response?.data?.data?.blockers;
      if (Array.isArray(blockers) && blockers.length > 0) {
        throw new CategoryDeleteBlockedError(
          error.response?.data?.message || 'Bu kategori silinemez.',
          blockers
        );
      }

      throw new Error(error.response?.data?.message || 'Kategori silinirken hata oluştu');
    }
  }

  /**
   * Kategorileri KALICI olarak siler (hard delete — geri alınamaz).
   *
   * Tekil deleteCategory'den farkı: alt kategori / bağlı ürün / e-ticaret engellerini
   * uygulamaz. Sunucu, hiyerarşi bağlarını kopardığı için silme sırası önemli değildir.
   */
  static async bulkHardDeleteCategories(companyId: string, categoryIds: string[]): Promise<number> {
    if (!categoryIds || categoryIds.length === 0) {
      return 0;
    }

    try {
      const response = await accountingApi.post(
        ACCOUNTING_API.INVENTORY_CATEGORY_BULK_HARD_DELETE,
        { ids: categoryIds }
      );
      console.log(`🗑️ [CATEGORY SERVICE] ${response.data?.count ?? 0} kategori kalıcı olarak silindi`);
      return response.data?.count ?? 0;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori kalıcı silme hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategoriler silinirken hata oluştu');
    }
  }

  /**
   * Şirketin TÜM kategorilerinin id'lerini döner.
   */
  static async getAllCompanyCategoryIds(companyId: string): Promise<string[]> {
    const categories = await this.getAllCategories(companyId);
    return categories.map(c => c.id).filter(Boolean);
  }

  /**
   * Verilen kategorileri kalıcı olarak siler, ilerlemeyi parça parça bildirir.
   */
  static async hardDeleteAllCategories(
    companyId: string,
    categoryIds: string[],
    onProgress?: (deleted: number, total: number) => void
  ): Promise<number> {
    const total = categoryIds.length;

    if (total === 0) {
      onProgress?.(0, 0);
      return 0;
    }

    const chunkSize = 100;
    let deleted = 0;

    for (let i = 0; i < categoryIds.length; i += chunkSize) {
      const chunk = categoryIds.slice(i, i + chunkSize);
      deleted += await this.bulkHardDeleteCategories(companyId, chunk);
      onProgress?.(deleted, total);
    }

    return deleted;
  }

  /**
   * Kategori ID'sine göre kategori getir
   */
  static async getCategoryById(companyId: string, categoryId: string): Promise<InventoryCategoryDto> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategori getiriliyor:', categoryId);
      const response = await accountingApi.get<InventoryCategoryDto>(
        ACCOUNTING_API.INVENTORY_CATEGORY_BY_ID(categoryId)
      );
      console.log('✅ [CATEGORY SERVICE] Kategori başarıyla getirildi:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] Kategori getirme hatası:', error);
      throw new Error(error.response?.data?.message || 'Kategori getirilirken hata oluştu');
    }
  }

  /**
   * Kategori e-ticaret durumunu değiştir - POST kullanıyoruz (sunucu kısıtlaması)
   */
  static async toggleEcommerceStatus(companyId: string, categoryId: string, enable: boolean): Promise<void> {
    try {
      console.log('🔍 [CATEGORY SERVICE] Kategori e-ticaret durumu değiştiriliyor:', { categoryId, enable });
      await accountingApi.post(
        ACCOUNTING_API.INVENTORY_CATEGORY_TOGGLE_ECOMMERCE(categoryId, enable)
      );
      console.log('✅ [CATEGORY SERVICE] Kategori e-ticaret durumu başarıyla değiştirildi');
    } catch (error: any) {
      console.error('❌ [CATEGORY SERVICE] E-ticaret durumu değiştirme hatası:', error);
      throw new Error(error.response?.data?.message || 'E-ticaret durumu değiştirilirken hata oluştu');
    }
  }
}

